import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs/Observable';
import { catchError, debounceTime, distinctUntilChanged, tap, switchMap, finalize, merge } from 'rxjs/operators';

import { OPTIONS } from 'OpsCenter/app.constants';
import { MerchantCategoriesService } from 'OpsCenter/merchants/merchant-categories.service';
import { DestinationService } from 'OpsCenter/common/services/destination.service';
import { UirouterMigrationService } from 'Utilities/uirouter.migration.service';
import { SeoService } from './seo.service';
import { Seo } from './seo.model';
import { of } from 'rxjs/internal/observable/of';

@Component({
  selector: 'app-seo-list',
  template: require('./seo.list.component.html'),
})

/** @desc List component to display seo's **/
export class SeoListComponent implements OnInit {
  isLoading = false;
  selectedCategories = [null, null, null];
  countryId: string;
  destinationId: string;
  destinationFound: boolean;
  destinationName: string;
  enquiryForm: FormGroup;
  categories: Array<object>;
  seos: Array<object>;
  hideSearchingWhenUnsubscribed = new Observable(() => () => (this.searching = false));
  searching = false;
  searchFailed = false;
  options = {
    languages: OPTIONS.locales.portal_languages,
  };

  matchAllBeforeThirdSlash = /^(?:[^/]*\/){2}([^/]*)/g;

  constructor(
    private fb: FormBuilder,
    private merchantCategoriesService: MerchantCategoriesService,
    private notificationService: ToastrService,
    private destinationService: DestinationService,
    private seoService: SeoService,
    private uirouterMigrationService: UirouterMigrationService,
  ) {
  }

  ngOnInit() {
    this.createForm();
    this.getCategories();
  }

  search = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => (this.searching = true)),
      switchMap(term =>
        this.destinationService.getDestinationSuggestion(term).pipe(
          tap(() => (this.searchFailed = false)),
          catchError(() => {
            this.searchFailed = true;
            return of([]);
          }),
        ),
      ),
      tap(() => (this.searching = false)),
      merge(this.hideSearchingWhenUnsubscribed),
    );
  formatter = (x: { name: string }) => x.browserUrl;

  /**
   * @desc Function to create seo enquiry form
   * @return {void}
   */
  createForm(): void {
    this.enquiryForm = this.fb.group({
      desinationPath: ['', [Validators.required, Validators.maxLength(120)]],
      categoryOne: new FormControl({ value: '', disabled: true }),
      categoryTwo: new FormControl({ value: '', disabled: true }),
      destinationType: new FormControl({ value: 'DESTINATION', disabled: false }),
      language: new FormControl({ value: 'en', disabled: false }),
    });
  }

  formatName(url): string {
    return url.replace(this.matchAllBeforeThirdSlash, '');
  }

  clearFilters(): void {
    this.enquiryForm.reset();
    this.createForm();
  }

  /**
   * @desc Function to retrieve and map merchant categories
   * @return {void}
   */
  getCategories(): void {
    this.isLoading = true;
    this.merchantCategoriesService
      .getAllCategories()
      .pipe(finalize(() => {
        this.isLoading = false;
      }))
      .subscribe(res => {
        if (res.categories) {
          this.categories = res.categories[0].children;
        }
      });
  }

  /**
   * @desc Function to retrieve destination
   * @return {void}
   */
  getDestinationId(suggestion): void {
    if (!suggestion && !this.enquiryForm.value.desinationPath.browserUrl) {
      return;
    }
    const browserUrl = suggestion ? suggestion.item.browserUrl : this.enquiryForm.value.desinationPath.browserUrl;
    this.isLoading = true;
    this.destinationService
      .getDestination(browserUrl)
      .pipe(finalize(() => {
        this.isLoading = false;
      }))
      .subscribe(
        res => {
          if (res) {
            this.destinationId = res[0].id;
            this.countryId = res[0].country.id;
            this.enquiryForm.get('categoryOne').enable();
            this.getSeos();
          }
        },
        error => {
          this.destinationFound = false;
        },
      );
  }

  /**
   * @desc Function to retrieve seos according to destination
   * @return {void}
   */
  getSeos(): void {
    this.isLoading = true;

    // Get choosen categories if applicable and map to parameters
    const categoriesIds = [];
    let categoryId = [];
    if (this.selectedCategories[0]) {
      this.selectedCategories.forEach(value => {
        if (value && value.id) {
          categoriesIds.push(value.id);
        }
      });
      categoryId = categoriesIds.slice(-1);
    }

    // Get seo's
    this.seoService
      .searchSeos(this.destinationId, categoryId, this.enquiryForm.value.destinationType, this.enquiryForm.value.language)
      .pipe(finalize(() => {
        this.isLoading = false;
      }))
      .subscribe(
        res => {
          if (res) {
            this.seos = res;
            this.destinationFound = true;
          }
        },
        () => {
          this.notificationService.error('Can\'t find seo text.');
        },
      );
  }

  /**
   * @desc Function to navigate to the seo edit page with given parameters
   * @TODO Once uirouter will get replaced, it needs to be moved to angular router instead of the helper
   * @param {object} seo - The SEO to be opened
   * @return {void}
   */
  goToSeo(seo: Seo): void {
    if (this.isLoading) {
      return;
    }
    const params = {
      categoryId: seo.categoryId,
      destinationId: seo.destinationId,
      countryId: this.countryId,
      language: seo.language,
    };
    this.uirouterMigrationService.goTo('app.seo_edit', params);
  }

  /**
   * @desc Function to handle change of dropdown values
   * @param {string} element - Identifier which element has been changed
   * @param {number} selection - The inde of the choosen category
   * @return {void}
   */
  handleChange(element: string, selection: number): void {
    switch (element) {
      case 'categoryOne':
        this.selectedCategories[0] = this.categories[selection];
        this.selectedCategories[1] = null;
        this.enquiryForm.get('categoryTwo').enable();
        this.getSeos();
        break;
      case 'categoryTwo':
        this.selectedCategories[1] = this.selectedCategories[0].children[selection];
        this.getSeos();
        break;
      case 'language':
        this.getSeos();
        break;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/seo/seo.list.component.ts